import React, { useEffect, useState, useRef } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const Map = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markerRef = useRef(null);
  
  // Get user data from Redux store
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  // Create custom marker element with user profile picture
  const createCustomMarker = (profilePictureUrl, userName) => {
    const markerElement = document.createElement('div');
    markerElement.className = 'custom-marker';
    
    // Create the marker HTML structure
    markerElement.innerHTML = `
      <div style="
        width: 50px;
        height: 50px;
        border-radius: 50%;
        border: 3px solid #1976d2;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        cursor: pointer;
        transition: transform 0.2s ease;
      ">
        ${profilePictureUrl ? 
          `<img src="${profilePictureUrl}" 
               alt="${userName || 'User'}" 
               style="
                 width: 100%;
                 height: 100%;
                 object-fit: cover;
                 border-radius: 50%;
               "
               onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
           />
           <div style="
             display: none;
             width: 100%;
             height: 100%;
             background: linear-gradient(135deg, #1976d2, #42a5f5);
             color: white;
             font-weight: bold;
             font-size: 18px;
             align-items: center;
             justify-content: center;
             border-radius: 50%;
           ">
             ${(userName || 'U').charAt(0).toUpperCase()}
           </div>` :
          `<div style="
             width: 100%;
             height: 100%;
             background: linear-gradient(135deg, #1976d2, #42a5f5);
             color: white;
             font-weight: bold;
             font-size: 18px;
             display: flex;
             align-items: center;
             justify-content: center;
             border-radius: 50%;
           ">
             ${(userName || 'U').charAt(0).toUpperCase()}
           </div>`
        }
      </div>
      <div style="
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 12px solid #1976d2;
        margin: -2px auto 0;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
      "></div>
    `;

    // Add hover effect
    markerElement.addEventListener('mouseenter', () => {
      markerElement.style.transform = 'scale(1.1)';
    });

    markerElement.addEventListener('mouseleave', () => {
      markerElement.style.transform = 'scale(1)';
    });

    return markerElement;
  };

  useEffect(() => {
    const initMap = () => {
      // const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
      const accessToken = 'pk.eyJ1IjoidnZsbGwyZmxyaSIsImEiOiJjbWpubzZkbWQxdG1jM2ZweXduYzFtYnJ4In0.-dkp_F8TZeROe6zxer-GSw';
      
      if (!accessToken) {
        setError('Mapbox access token is not configured');
        setLoading(false);
        return;
      }

      // Set the access token
      mapboxgl.accessToken = accessToken;

      // Check if container exists
      if (!mapContainerRef.current) {
        setError('Map container not found');
        setLoading(false);
        return;
      }

      try {
        // Create map instance
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [139.6503, 35.6762], // Tokyo coordinates [lng, lat]
          zoom: 12
        });

        // Add navigation controls
        mapRef.current.addControl(new mapboxgl.NavigationControl());

        // Add fullscreen control
        mapRef.current.addControl(new mapboxgl.FullscreenControl());

        // Get user's location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userLocation = [position.coords.longitude, position.coords.latitude];

              // Update map center to user's location
              mapRef.current.setCenter(userLocation);

              // Create custom marker element with user profile picture
              const markerElement = createCustomMarker(
                user?.profilePicture, 
                user?.name || user?.username || user?.email
              );

              // Create and add custom marker for user's location
              markerRef.current = new mapboxgl.Marker({
                element: markerElement,
                anchor: 'bottom'
              })
                .setLngLat(userLocation)
                .setPopup(
                  new mapboxgl.Popup({ 
                    offset: [0, -20],
                    closeButton: false,
                    className: 'custom-popup'
                  }).setHTML(`
                    <div style="
                      padding: 12px;
                      text-align: center;
                      min-width: 200px;
                    ">
                      <div style="
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        margin-bottom: 8px;
                      ">
                        ${user?.profilePicture ? 
                          `<img src="${user.profilePicture}" 
                               alt="${user?.name || 'User'}"
                               style="
                                 width: 40px;
                                 height: 40px;
                                 border-radius: 50%;
                                 object-fit: cover;
                                 border: 2px solid #1976d2;
                               "
                           />` :
                          `<div style="
                             width: 40px;
                             height: 40px;
                             border-radius: 50%;
                             background: linear-gradient(135deg, #1976d2, #42a5f5);
                             color: white;
                             display: flex;
                             align-items: center;
                             justify-content: center;
                             font-weight: bold;
                             border: 2px solid #1976d2;
                           ">
                             ${(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
                           </div>`
                        }
                        <div style="text-align: left;">
                          <h3 style="
                            margin: 0;
                            font-size: 16px;
                            color: #333;
                            font-weight: 600;
                          ">
                            ${user?.name || user?.username || 'Your Location'}
                          </h3>
                          <p style="
                            margin: 4px 0 0 0;
                            font-size: 12px;
                            color: #666;
                          ">
                            ${isAuthenticated ? 'You are here!' : 'Current location'}
                          </p>
                        </div>
                      </div>
                      <div style="
                        font-size: 11px;
                        color: #888;
                        border-top: 1px solid #eee;
                        padding-top: 8px;
                        margin-top: 8px;
                      ">
                        Lat: ${position.coords.latitude.toFixed(6)}<br>
                        Lng: ${position.coords.longitude.toFixed(6)}
                      </div>
                    </div>
                  `)
                )
                .addTo(mapRef.current);

              // Fly to user's location with smooth animation
              mapRef.current.flyTo({
                center: userLocation,
                zoom: 15,
                duration: 2000
              });

              setLoading(false);
            },
            (error) => {
              console.error('Geolocation error:', error);
              setError('Error getting your location: ' + error.message);
              setLoading(false);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        } else {
          setError('Geolocation is not supported by your browser');
          setLoading(false);
        }

        // Handle map load event
        mapRef.current.on('load', () => {
          if (!navigator.geolocation) {
            setLoading(false);
          }
        });

        // Handle map errors
        mapRef.current.on('error', (e) => {
          console.error('Mapbox error:', e);
          setError('Error loading map: ' + (e.error?.message || 'Unknown error'));
          setLoading(false);
        });

      } catch (err) {
        console.error('Map initialization error:', err);
        setError('Error initializing map: ' + err.message);
        setLoading(false);
      }
    };

    initMap();

    // Cleanup function
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
      }
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [user, isAuthenticated]); // Add user to dependencies to re-render when user data changes

  return (
    <Paper sx={{ height: '100%', p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Location Map
        {isAuthenticated && user?.name && (
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
            Welcome, {user.name}!
          </Typography>
        )}
      </Typography>
      <Box sx={{ height: 'calc(100% - 60px)', position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2,
              p: 2
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Loading map...
            </Typography>
          </Box>
        )}
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 2,
              p: 3,
              maxWidth: '80%',
              textAlign: 'center'
            }}
          >
            <Typography color="error" variant="h6" gutterBottom>
              Map Error
            </Typography>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}
        <Box 
          ref={mapContainerRef}
          sx={{ 
            height: '100%', 
            width: '100%',
            borderRadius: 1,
            overflow: 'hidden',
            '& .mapboxgl-popup-content': {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              border: 'none',
              padding: 0
            },
            '& .mapboxgl-popup-tip': {
              borderTopColor: '#fff'
            },
            '& .custom-popup .mapboxgl-popup-content': {
              maxWidth: '280px'
            },
            '& .custom-marker': {
              cursor: 'pointer'
            }
          }} 
        />
      </Box>
    </Paper>
  );
};

export default Map;